import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileDown, ArrowRight, FileText, SlidersHorizontal } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useContent, resolveImg, isLocalImg } from "@/lib/content";
import PageHero from "@/components/site/PageHero";

const EASE = [0.16, 1, 0.3, 1];
const show = (v) => (v && String(v).trim() ? v : "Sob consulta");

export default function CarregadoresPage() {
  useSeo(
    "Carregadores para Veículos Elétricos | Carregador DC e Eletropostos — HB Charger",
    "Catálogo HB Charger: carregadores rápidos DC e estações de recarga para veículos elétricos. Ficha técnica, potências sob consulta e projeto personalizado."
  );
  const { chargers } = useContent();

  return (
    <>
      <PageHero
        testId="chargers-page-title"
        eyebrow="HB Charger — Catálogo"
        title="Carregadores para veículos elétricos"
        desc="Modelos em diferentes faixas de potência para cada aplicação. Determinados equipamentos permitem configuração e limitação da potência conforme a infraestrutura elétrica disponível no local. Valores comerciais sob consulta."
      />

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div data-testid="chargers-catalog" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chargers.map((ch, i) => (
              <motion.article
                key={ch.id || i}
                data-testid={`charger-card-${ch.id || i}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }}
                className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white hover:shadow-[0_28px_56px_rgba(11,25,44,0.12)] transition-shadow duration-500"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={resolveImg(ch.foto)}
                    alt={`${ch.nome} — ${ch.linha}`}
                    loading="lazy"
                    className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${
                      isLocalImg(ch.foto) ? "object-contain bg-white p-3" : "object-cover"
                    }`}
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-[#0B192C]/90 backdrop-blur px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400">
                    {ch.linha}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h2 className="font-display text-2xl font-bold tracking-tight text-[#0B192C]">{ch.nome}</h2>
                  <dl className="mt-5 space-y-2.5 text-sm">
                    {[
                      ["Potência", ch.potencia],
                      ["Conectores", ch.conectores],
                      ["Voltagem", ch.voltagem],
                      ["Tensão de saída", ch.tensao],
                      ["Comunicação", ch.comunicacao],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-4 border-b border-slate-100 pb-2.5">
                        <dt className="font-semibold text-slate-500">{k}</dt>
                        <dd className={`font-bold text-right ${v ? "text-[#0B192C]" : "text-amber-600"}`}>{show(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-auto flex flex-col gap-2.5 pt-6">
                    <Link
                      data-testid={`charger-datasheet-${ch.id || i}`}
                      to={`/carregadores/${ch.id || i}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0B192C] text-[#0B192C] font-extrabold text-sm px-5 py-3 hover:bg-[#0B192C] hover:text-white transition-colors duration-300"
                    >
                      <FileText className="h-4 w-4" />
                      Ver ficha técnica
                    </Link>
                    <Link
                      data-testid={`charger-quote-${ch.id || i}`}
                      to={`/contato?produto=${encodeURIComponent(ch.nome)}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0B192C] font-extrabold text-sm px-5 py-3 transition-colors duration-300"
                    >
                      Solicitar orçamento
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-14 grid lg:grid-cols-2 gap-5"
          >
            <div className="flex items-start gap-4 rounded-3xl bg-[#0B192C] p-8">
              <SlidersHorizontal className="h-8 w-8 shrink-0 text-amber-400" />
              <div>
                <h3 className="font-display text-lg font-bold text-white">Potência configurável</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Determinados modelos permitem configuração e limitação da potência de
                  carregamento conforme a infraestrutura elétrica disponível — ideal para
                  adequar o equipamento à realidade do seu empreendimento.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 rounded-3xl border border-amber-300 bg-amber-50 p-8">
              <div>
                <h3 className="font-display text-lg font-bold text-[#0B192C]">Catálogo completo em PDF</h3>
                <p className="mt-1 text-sm text-slate-600">Baixe o Catálogo de Carregadores HB Charger.</p>
              </div>
              <a
                data-testid="catalog-download-button"
                href="/catalogo-hb-charger.pdf"
                download
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#0B192C] px-6 py-3.5 text-sm font-extrabold text-white hover:bg-[#12243d] transition-colors duration-300"
              >
                <FileDown className="h-4 w-4 text-amber-400" />
                Baixar catálogo
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
